import { FC, useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { WeightByModalityResponse } from './types';
import ChartErrorState from './ChartErrorState';
import { monthAbbreviations } from '../../utils/localeFormat';
import { modalityColors, chartChrome } from '../../utils/chartColors';
import './WeightByModalityChart.scss';

interface WeightByModalityChartProps {
  data: WeightByModalityResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}


const WeightByModalityChart: FC<WeightByModalityChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  // memoised so it can be an effect dependency without re-firing every render
  const monthNames = useMemo(() => monthAbbreviations(lng), [lng]);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  // Pivot data: group by month, then by modality
  const pivotedData = useMemo(() => {
    if (!data || data.data.length === 0) return null;

    const monthsSet = new Set<string>();
    const modalitiesSet = new Set<string>();
    const dataMap = new Map<string, Map<string, number>>();

    data.data.forEach((item) => {
      monthsSet.add(item.month);
      modalitiesSet.add(item.modality);
      if (!dataMap.has(item.month)) dataMap.set(item.month, new Map());
      const monthMap = dataMap.get(item.month);
      if (monthMap) monthMap.set(item.modality, item.weightTons);
    });

    const months = Array.from(monthsSet).sort();
    const modalities = Array.from(modalitiesSet);

    return { months, modalities, dataMap };
  }, [data]);

  useEffect(() => {
    if (!chartNode || !pivotedData) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const { months, modalities, dataMap } = pivotedData;

    const modality = modalityColors();
    const chrome = chartChrome();

    const monthLabels = months.map((m) => {
      const monthIndex = parseInt(m.split('-')[1], 10) - 1;
      return monthNames[monthIndex] || m;
    });

    const series: echarts.BarSeriesOption[] = modalities.map((modalityName, idx) => ({
      type: 'bar',
      name: modalityName,
      data: months.map((m) => dataMap.get(m)?.get(modalityName) ?? 0),
      itemStyle: { color: modality[idx % modality.length] },
      barMaxWidth: 40,
      label: {
        show: true,
        position: 'top',
        fontSize: 10,
        formatter: (params: any) => (params.value > 0 ? String(Math.round(params.value)) : ''),
      },
    }));

    const option: echarts.EChartsOption = {
      tooltip: { trigger: 'axis' },
      legend: {
        bottom: 0,
        textStyle: { fontSize: 12 },
      },
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: monthLabels,
        axisLabel: { fontSize: 12, color: chrome.muted },
      },
      yAxis: {
        type: 'value',
        name: String(t('cpi.axis.weightTons')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { color: chrome.muted },
      },
      series,
    };

    chartInstance.current.setOption(option, true);
  }, [pivotedData, chartNode, lng, monthNames, t]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="weight-by-modality-chart">
        <h3 className="weight-by-modality-chart__title">{t('cpi.analytics.weightByModality')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="weight-by-modality-chart weight-by-modality-chart--loading">
        <h3 className="weight-by-modality-chart__title">{t('cpi.analytics.weightByModality')}</h3>
        <div className="weight-by-modality-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="weight-by-modality-chart">
        <h3 className="weight-by-modality-chart__title">{t('cpi.analytics.weightByModality')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="weight-by-modality-chart">
      <h3 className="weight-by-modality-chart__title">{t('cpi.analytics.weightByModality')}</h3>
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
    </div>
  );
};

export default WeightByModalityChart;
